// What a rules change, a threshold preview or a base switch hands back, and the shape each
// card draws from.
//
// rgip's `presentation.py` puts these on the `activity_end` of the tool, under
// `receipt.card`. Nothing on them is phrased here: the rows, the tier tables and the
// sentences are the tool's own. A rules card and a thresholds card draw on the PREVIEW as
// well as the write, because the preview is where a change gets reviewed; `state` says
// which, and the card labels it so a proposal cannot read as a receipt.

/** One rule a change touches. `was` and `now` are points; null on the side it is absent. */
export interface RuleChangeRow {
  group: number;
  name?: string | null;
  /** What the rule tests, in words. Null on a row that renames the group. */
  rule?: string | null;
  was?: number | string | null;
  now?: number | string | null;
  change: 'added' | 'removed' | 'changed' | 'renamed';
  /** The agent's evidence for the points, shown beside the rule and never stored. */
  why?: string | null;
  /** Values the rule names that the training data never holds: it can never fire on them. */
  not_in_data?: string[];
}

/**
 * The model pages a card links to: where the thing it changed lives. Passed to the
 * panel's focus handler as a `page` target, so a card needs no navigation of its own.
 */
export type ModelPageTarget = 'rules' | 'thresholds' | 'signals';

export interface PointsRange {
  min: number;
  max: number;
}

/** One tier of a tier table, as rgip's ensembling module reports it. */
export interface TierRow {
  tier: string;
  leads_pct?: number;
  share_of_all_conversions_pct?: number;
  vs_average?: number | null;
}

/** A rule based model's rules, changed or about to be. */
export interface RulesCardData {
  kind: 'rules';
  state: 'proposed' | 'saved';
  rows: RuleChangeRow[];
  range_before?: PointsRange | null;
  range_after?: PointsRange | null;
  /** What the thresholds deliver now, as a sentence. Saved only. */
  says?: string | null;
}

/** One set of cutoffs and what it delivers. */
export interface TierColumn {
  thresholds?: Record<string, number> | null;
  tiers: TierRow[];
  /** Every tier converts better than the one below it. Null when it was not checked. */
  staggers?: boolean | null;
}

/** A rule based model's cutoffs: today's, and one proposal. */
export interface ThresholdsCardData {
  kind: 'thresholds';
  state: 'proposed' | 'saved';
  current: TierColumn;
  proposed: TierColumn;
}

/** A model just switched between trees and rules. */
export interface BaseCardData {
  kind: 'base';
  to?: string;
  /** What the switch did not change, in the tool's words. */
  thresholds_kept?: string;
}

function isObject(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

/** The rules card a receipt carries, or null when it carries none. */
export function rulesCardFrom(receipt: unknown): RulesCardData | null {
  if (!isObject(receipt)) return null;
  const card = receipt.card;
  if (!isObject(card) || card.kind !== 'rules') return null;
  if (!Array.isArray(card.rows) || !card.rows.length) return null;
  return card as unknown as RulesCardData;
}

/** The thresholds card a receipt carries, or null when it carries none. */
export function thresholdsCardFrom(
  receipt: unknown
): ThresholdsCardData | null {
  if (!isObject(receipt)) return null;
  const card = receipt.card;
  if (!isObject(card) || card.kind !== 'thresholds') return null;
  const proposed = card.proposed;
  if (!isObject(proposed) || !Array.isArray(proposed.tiers)) return null;
  if (!proposed.tiers.length) return null;
  return card as unknown as ThresholdsCardData;
}

/** The base card a receipt carries, or null when it carries none. */
export function baseCardFrom(receipt: unknown): BaseCardData | null {
  if (!isObject(receipt)) return null;
  const card = receipt.card;
  if (!isObject(card) || card.kind !== 'base' || !card.to) return null;
  return card as unknown as BaseCardData;
}

/** `trees` / `points` as a reader says it. */
export function baseWord(base: string | undefined): string {
  return base && base !== 'trees' ? 'rules' : 'trees';
}

/** `+40`, `-10`, `0`; a dash where a rule is absent on that side. */
export function pointsText(points: number | string | null | undefined): string {
  if (points === null || points === undefined || points === '') return '-';
  const n = Number(points);
  if (!Number.isFinite(n)) return String(points);
  return n > 0 ? `+${n}` : String(n);
}

/** A group as the Rules page names it: its name, or what number it is. */
export function groupText(row: Pick<RuleChangeRow, 'group' | 'name'>): string {
  return row.name?.trim() || `Group ${row.group}`;
}

/** `250 / 180 / 130`, Very Good to Medium, the order the cutoffs are read in. */
export function cutoffsText(
  thresholds: Record<string, number> | null | undefined
): string {
  if (!thresholds) return '-';
  return ['very good', 'good', 'medium']
    .map((tier) =>
      typeof thresholds[tier] === 'number' ? String(thresholds[tier]) : '?'
    )
    .join(' / ');
}

/**
 * One turn's threshold previews, merged into one comparison.
 *
 * The agent tries candidates one preview at a time, and three cards stacked in a bubble
 * are three tables to hold side by side, which is the prose problem again. So the turn
 * keeps ONE comparison: today's column once, then a column per candidate in the order
 * they were tried. A candidate tried twice replaces its earlier column rather than
 * repeating it.
 */
export interface ThresholdsComparison {
  current: TierColumn;
  candidates: Array<TierColumn & { state: 'proposed' | 'saved' }>;
}

export function mergeThresholds(
  comparison: ThresholdsComparison | null,
  card: ThresholdsCardData
): ThresholdsComparison {
  const candidate = { ...card.proposed, state: card.state };
  const key = cutoffsText(card.proposed.thresholds);
  if (!comparison) return { current: card.current, candidates: [candidate] };
  const candidates = comparison.candidates.filter(
    (c) => cutoffsText(c.thresholds) !== key
  );
  return {
    current: comparison.current,
    candidates: [...candidates, candidate],
  };
}
